import { Bodies, World, type Body } from "fast-2d";
import { Camera } from "../render/Camera";
import { CanvasRenderer } from "../render/CanvasRenderer";

const FIXED_DT = 1 / 60;

export const FIRST_SIMULATION_CODE = `import { World, Bodies } from "fast-2d";

const world = new World({ gravity: [0, 9.81] });
world.add(Bodies.rectangle({ position: [0, 0.5], width: 8, height: 1, isStatic: true }));
const box = world.add(Bodies.rectangle({ position: [0, -4], width: 1, height: 1 }));

let acc = 0, last = 0;
for (let frame = 1; frame <= 120; frame++) {
	const now = frame / 60; acc += now - last; last = now;
	while (acc >= 1 / 60) { world.step(1 / 60); acc -= 1 / 60; }
	if (frame % 30 === 0) console.log(box.position.y.toFixed(2));
}`;

export interface FirstSimulationScene {
	world: World;
	box: Body;
	camera: Camera;
}

export function createFirstSimulationScene(): FirstSimulationScene {
	const world = new World({ gravity: [0, 9.81] });
	world.add(Bodies.rectangle({ position: [0, 0.5], width: 8, height: 1, isStatic: true }));
	const box = world.add(Bodies.rectangle({ position: [0, -4], width: 1, height: 1 }));
	return { world, box, camera: new Camera(0, -1.8, 52) };
}

export function simulateFirstSimulationFrames(frames = 120): number {
	const { world, box } = createFirstSimulationScene();
	let acc = 0;
	let last = 0;
	for (let frame = 1; frame <= frames; frame++) {
		const now = frame / 60;
		acc += now - last;
		last = now;
		while (acc >= FIXED_DT) {
			world.step(FIXED_DT);
			acc -= FIXED_DT;
		}
	}
	return box.position.y;
}

export function initFirstSimulationPlayground(): void {
	const codeEl = document.getElementById("first-sim-code");
	const canvas = document.getElementById("first-sim-canvas") as HTMLCanvasElement | null;
	const output = document.getElementById("first-sim-output");
	const copyBtn = document.getElementById("first-sim-copy") as HTMLButtonElement | null;
	if (!codeEl || !canvas || !output) return;

	codeEl.textContent = FIRST_SIMULATION_CODE;
	bindCopy(copyBtn, FIRST_SIMULATION_CODE, "Copy code");

	const renderer = new CanvasRenderer(canvas);
	let scene = createFirstSimulationScene();
	let last = performance.now();
	let acc = 0;
	let raf = 0;
	let running = false;
	let settledFor = 0;

	const resize = (): void => renderer.resize(scene.camera);
	new ResizeObserver(resize).observe(canvas);
	resize();

	const reset = (): void => {
		scene = createFirstSimulationScene();
		acc = 0;
		settledFor = 0;
		resize();
	};

	const tick = (now: number): void => {
		if (!running) return;
		const dt = Math.min((now - last) / 1000, 0.1);
		last = now;
		acc += dt;
		while (acc >= FIXED_DT) {
			scene.world.step(FIXED_DT);
			acc -= FIXED_DT;
		}
		if (scene.box.position.y > -0.52 && Math.abs(scene.box.velocity.y) < 0.04) settledFor += dt;
		else settledFor = 0;
		if (settledFor > 1.1) reset();
		renderer.render(scene.world, scene.camera);
		output.textContent = `box.y = ${scene.box.position.y.toFixed(2)}`;
		raf = requestAnimationFrame(tick);
	};

	const start = (): void => {
		if (running) return;
		running = true;
		last = performance.now();
		raf = requestAnimationFrame(tick);
	};
	const stop = (): void => {
		running = false;
		cancelAnimationFrame(raf);
	};

	new IntersectionObserver(
		(entries) => {
			if (entries.some((entry) => entry.isIntersecting)) start();
			else stop();
		},
		{ threshold: 0 },
	).observe(canvas);
}

function bindCopy(btn: HTMLButtonElement | null, text: string, resetText: string): void {
	if (!btn) return;
	let timer: number | undefined;
	btn.addEventListener("click", async () => {
		try {
			await navigator.clipboard.writeText(text);
			btn.textContent = "Copied";
		} catch {
			btn.textContent = "Press Ctrl+C";
		}
		window.clearTimeout(timer);
		timer = window.setTimeout(() => {
			btn.textContent = resetText;
		}, 1800);
	});
}